import { Badge, Box, Text, VStack } from '@chakra-ui/react'
import type { CaseRequest, QueueEmail } from '@/lib/services/caseThreadsService'
import { caseThreadDisplayStatus } from '@/lib/utils/caseThreads'

type Props = {
  requests: CaseRequest[]
  queue: QueueEmail[] | null
  selectedRequestId: number | null
  selectedEmailId: number | null
  onSelectRequest: (id: number) => void
  onSelectEmail: (id: number) => void
  parentLabels?: Readonly<Record<number, string>>
}

export function RequestList({ requests, queue, selectedRequestId, selectedEmailId, onSelectRequest, onSelectEmail, parentLabels = {} }: Props) {
  if (queue) {
    if (queue.length === 0) {
      return (
        <Text fontSize="sm" color="gray.500" p={4}>
          This queue is empty.
        </Text>
      )
    }
    return (
      <VStack align="stretch" spacing={1} p={2} overflowY="auto" h="100%">
        {queue.map((email) => (
          <Box
            key={email.id}
            p={2}
            borderWidth="1px"
            borderRadius="md"
            cursor="pointer"
            bg={selectedEmailId === email.id ? 'orange.50' : 'transparent'}
            onClick={() => onSelectEmail(email.id)}
          >
            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
              {email.subject || '(No subject)'}
            </Text>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {email.from_name || email.from_email}
            </Text>
            <Text fontSize="xs" noOfLines={2}>
              {email.body_preview}
            </Text>
            {email.reason ? (
              <Badge mt={1} colorScheme="orange">
                {email.reason}
              </Badge>
            ) : null}
          </Box>
        ))}
      </VStack>
    )
  }

  if (requests.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" p={4}>
        Select a client or requestor to see ticket requests.
      </Text>
    )
  }

  return (
    <VStack align="stretch" spacing={1} p={2} overflowY="auto" h="100%">
      {requests.map((request) => (
        <Box
          key={request.id}
          p={2}
          borderWidth="1px"
          borderRadius="md"
          cursor="pointer"
          bg={selectedRequestId === request.id ? 'blue.50' : 'transparent'}
          onClick={() => onSelectRequest(request.id)}
        >
          <Text fontSize="xs" color="gray.500">
            {request.request_number}
            {request.unread_count > 0 ? ` · ${request.unread_count} unread` : ''}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>
            {request.subject || request.preview || 'Ticket request'}
          </Text>
          <Badge textTransform="none">{caseThreadDisplayStatus(request, parentLabels)}</Badge>
          {request.needs_human_review ? (
            <Badge ml={1} colorScheme="orange">
              Needs attention
            </Badge>
          ) : null}
        </Box>
      ))}
    </VStack>
  )
}
